import { SearchNormal1 } from 'iconsax-react';
import { useTranslation } from 'next-i18next';
import React, { JSX, useMemo, useRef } from 'react';

import Input from '@/components/Input';
import optionStyles from '@/components/Select/SingleSelectOption/SelectOption.module.scss';
import Spinner from '@/components/Spinner';
import classNames from '@/helpers/classNames';

import InputErrorMessage from '../InputErrorMessage';
import useSelect from './hooks';
import styles from './Select.module.scss';
import SelectButton from './SelectButton';
import { BaseSelectOptionProps, BaseSelectProps } from './types';
import { useDropdownPosition } from './useDropdownPosition';

export default function BaseSelect<T>(
  props: BaseSelectProps<T> & {
    optionComponent: (props: BaseSelectOptionProps<T>) => JSX.Element;
  }
) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const selectButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('common');
  const {
    options = [],
    className,
    label,
    LabelWrapper,
    isRequiredIndicatorVisible,
    name,
    buttonText = t('select') ?? '',
    drawerClassName,
    withSearch,
    customSelectButton,
    activeElement,
    optionComponent,
    onSelect,
    closeOnSelect,
    fullWidth,
    searchPlaceholder,
    buttonContent,
    additionalButtonContent,
    onSearch,
    searchText: outsideSearchText,
    labelClassName,
    error,
    selectButtonVariant,
    isMultiSelect,
    labelVariant = '',
    labelWeight = 'regular',
    disabled,
    isPending,
    onReset,
    optionsFooter,
    minSpaceBuffer,
    disableOpenUpward,
    fitContentHeight,
  } = props;
  const {
    isOpen,
    openOptions,
    search,
    searchText,
    filteredOptions,
    closeOptions,
  } = useSelect(drawerRef, selectButtonRef, options);

  const { openUpward, maxHeight } = useDropdownPosition(
    isOpen,
    selectButtonRef,
    drawerRef,
    minSpaceBuffer,
    disableOpenUpward,
    fitContentHeight
  );

  const selectOption = (option: T, selected: boolean) => {
    onSelect(option, selected);

    if (closeOnSelect) {
      closeOptions();
    }
  };

  function focusOnFirstOption() {
    (optionsRef.current?.firstChild as HTMLElement)?.focus();
  }

  function focusOnLastOption() {
    (optionsRef.current?.lastChild as HTMLElement)?.focus();
  }

  const handleKeyDown =
    (option: T, selected: boolean) => (e: React.KeyboardEvent) => {
      switch (e.key) {
        case ' ':
        case 'SpaceBar':
        case 'Enter':
          e.preventDefault();
          selectOption(option, selected);
          break;
        case 'Esc':
        case 'Escape':
          e.preventDefault();
          closeOptions();
          break;
        case 'Up':
        case 'ArrowUp': {
          e.preventDefault();
          const target = e.target as HTMLElement;
          const previousElement = target.previousElementSibling as HTMLElement;

          if (previousElement) {
            previousElement?.focus();
          } else {
            focusOnLastOption();
          }
          break;
        }
        case 'Down':
        case 'ArrowDown': {
          const target = e.target as HTMLElement;
          const nextElement = target.nextElementSibling as HTMLElement;

          if (nextElement) {
            nextElement?.focus();
          } else {
            focusOnFirstOption();
          }

          break;
        }
        case 'Tab':
          e.preventDefault();
          break;
        case 'PageUp':
        case 'Home':
          e.preventDefault();
          focusOnFirstOption();
          break;
        case 'PageDown':
        case 'End':
          e.preventDefault();
          focusOnLastOption();
          return;
        default:
          break;
      }
    };

  const selected = activeElement && String(activeElement);

  const labelComponent = useMemo(
    () =>
      label && (
        <label
          htmlFor={name}
          className={classNames([
            styles.label,
            styles[labelVariant],
            styles[labelWeight],
            labelClassName,
          ])}
        >
          {label}
          {isRequiredIndicatorVisible ? ' *' : ''}
        </label>
      ),
    [
      isRequiredIndicatorVisible,
      label,
      labelClassName,
      labelVariant,
      labelWeight,
      name,
    ]
  );

  return (
    <div
      className={classNames([styles.container, className], {
        [styles.fullWidth]: fullWidth,
        [styles.error]: error,
      })}
    >
      {LabelWrapper ? (
        <LabelWrapper>{labelComponent}</LabelWrapper>
      ) : (
        <>{labelComponent}</>
      )}
      <div className={styles.selectWrapper}>
        <SelectButton
          ref={selectButtonRef}
          name={name}
          isOpen={isOpen}
          buttonText={buttonText}
          buttonContent={buttonContent}
          additionalButtonContent={additionalButtonContent}
          customSelectButton={customSelectButton}
          onClick={openOptions}
          selected={selected}
          error={error}
          onReset={onReset}
          variant={selectButtonVariant}
          disabled={disabled}
        />
        {isOpen && !disabled && (
          <div
            ref={drawerRef}
            className={classNames([styles.drawer, drawerClassName], {
              [styles.openUpward]: openUpward,
            })}
            style={maxHeight ? { maxHeight } : undefined}
          >
            {withSearch && (
              <div className={styles.searchWrapper}>
                <Input
                  name="select-search"
                  LeftIcon={SearchNormal1}
                  placeholder={searchPlaceholder || t('search')}
                  value={(onSearch ? outsideSearchText : searchText) ?? ''}
                  autoComplete="off"
                  onChange={(event) => {
                    const method = onSearch ?? search;

                    return method(event.target.value);
                  }}
                  className={styles.searchInputWrapper}
                  inputClassName={styles.searchInput}
                  autoFocus
                />
              </div>
            )}
            <ul
              ref={optionsRef}
              id={`${name}_dropdown`}
              className={styles.options}
              role="listbox"
              aria-activedescendant={`${name}_element_${String(selected)}`}
              aria-multiselectable={isMultiSelect}
              tabIndex={-1}
            >
              {filteredOptions.length ? (
                filteredOptions.map((option, index) => {
                  const shouldFocus =
                    !withSearch &&
                    (activeElement
                      ? activeElement === option.value
                      : index === 0);

                  const shouldScrollTo =
                    withSearch && activeElement
                      ? activeElement === option.value
                      : !!option.scrollTo;

                  return optionComponent({
                    key: index,
                    option,
                    onClick: selectOption,
                    onKeyDown: handleKeyDown,
                    shouldFocus,
                    shouldScrollTo,
                  });
                })
              ) : (
                <li
                  className={classNames([optionStyles.option, styles.readOnly])}
                >
                  {t('no-options')}
                </li>
              )}
              {optionsFooter}
            </ul>
            {typeof isPending === 'boolean' && (
              <div
                className={classNames([styles.loader], {
                  [styles.withSearch]: withSearch,
                  [styles.visible]: isPending,
                })}
              >
                <Spinner />
              </div>
            )}
          </div>
        )}
      </div>
      <InputErrorMessage error={error} />
    </div>
  );
}
